import { useEffect, useMemo, useState } from 'react';

import Papa, { ParseResult } from 'papaparse';
import styled from 'styled-components';

import { ActionText } from '@components/action-text';
import { Alert } from '@components/alert';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { Checkbox } from '@components/checkbox';
import { FileField } from '@components/file-field';
import { FormError } from '@components/form-error';
import { IconButton } from '@components/icon-button';
import { IconArrowRight } from '@components/icons/arrow-right';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconDownload } from '@components/icons/download';
import { IconFile } from '@components/icons/file';
import { IconOpen } from '@components/icons/open';
import { IconUpload } from '@components/icons/upload';
import { RelativeSpinner, Spinner } from '@components/loading-spinner';
import { hideModal } from '@components/modal';
import { Select } from '@components/select';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Stepper } from '@components/stepper';
import { DANGEROUS_START_CHARACTERS } from '@components/table/export-csv';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { getFileExtension } from '@utils/browser';
import { pxToRem } from '@utils/converters';
import { camelize, startsWith } from '@utils/strings';

import { AttributeType, TravellerAttributeTemplate } from 'api/attributes/requests';
import { BulkCreateTraveller, BulkCreateTravellerBase } from 'api/traveller-journeys/types';
import { ImportColumn } from 'api/types';

import { companyTravellerAttributePrefix, journeyAttributePrefix } from '../../helpers';

export const ImportToolStepper = ({ currentIndex }: { currentIndex: number }) => {
  const { t } = useScopedTranslate(locale);
  return (
    <Stepper
      items={[
        { label: t('gettingStarted') },
        { label: t('uploadFile') },
        { label: t('matchColumns') },
        { label: t('import') },
      ]}
      activeIndex={currentIndex}
      showCompleted={true}
    />
  );
};

export type StepType =
  | 'instruction'
  | 'gettingStarted'
  | 'uploadFile'
  | 'matchColumns'
  | 'import'
  | 'importing';

export const Instruction = ({
  setStep,
  download,
  templateName,
  isJourney,
  csvTemplateInfo,
  uploadcsvInfo,
  helpPageUrl,
}: {
  setStep: (value: StepType) => void;
  download: () => void;
  templateName: string;
  isJourney?: boolean;
  csvTemplateInfo?: string;
  uploadcsvInfo?: string;
  helpPageUrl?: string;
}) => {
  const { t } = useScopedTranslate(locale);
  const helpUrl =
    helpPageUrl ??
    'https://help.journeys.50skills.app/en/articles/166922-bulk-import-format-guidelines';

  const csvTemplateDescription = csvTemplateInfo
    ? csvTemplateInfo
    : isJourney
    ? t('csvTemplateDescriptionJourney')
    : t('csvTemplateDescription');

  const uploadcsvDescription = uploadcsvInfo
    ? uploadcsvInfo
    : isJourney
    ? t('uploadCSVDescriptionJourney')
    : t('uploadCSVDescription');
  return (
    <Stack space={6}>
      <Stack space={4}>
        <div>
          <Text text={`1. ${t('formatGuidelines')}`} font="bold14" />
          <Text text={t('formatDescription')} font="regular14" color="textSecondary" />
        </div>
        <ActionText.ExternalLink href={helpUrl} color="textSecondary" font="regular14" underline>
          {t('formatGuidelines')}
        </ActionText.ExternalLink>
      </Stack>
      <Separator />
      <Stack space={4}>
        <div>
          <Text text={`2. ${t('csvTemplate')}`} font="bold14" />
          <Text text={csvTemplateDescription} font="regular14" color="textSecondary" />
        </div>
        <Stack orientation="horizontal">
          <Card space={4}>
            <Stack align="center" space={2} orientation="horizontal">
              <IconFile />
              <Text font="regular14" text={templateName} />
              <IconButton
                variant="ghost-purple"
                icon={<IconDownload dataTestid="download" />}
                onClick={() => download()}
              />
            </Stack>
          </Card>
        </Stack>
      </Stack>
      <Separator />
      <Stack space={4}>
        <div>
          <Text text={`3. ${t('uploadCSV')}`} font="bold14" />
          <Text text={uploadcsvDescription} font="regular14" color="textSecondary" />
        </div>
        <Stack space={4} orientation="horizontal" justify="flex-end">
          <Button variant="secondary" text={t('core.cancel')} onClick={hideModal} />
          <Button
            text={t('openImportTool')}
            iconLeft={<IconOpen />}
            onClick={() => setStep('gettingStarted')}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export const GettingStarted = ({
  setStep,
  attributes,
}: {
  setStep: (value: StepType) => void;
  attributes: (AttributeType | TravellerAttributeTemplate)[];
}) => {
  const { t } = useScopedTranslate(locale);
  const baseItems = [
    {
      column: t('fullName'),
      format: t('stringText'),
      example: 'James Smith',
      required: true,
    },
    {
      column: t('email'),
      format: t('stringText'),
      example: 'james@email.com',
      required: true,
    },
    {
      column: t('core.phone'),
      format: t('number'),
      example: '+354 600 8000',
      required: false,
    },
  ];
  const filteredAttributes = attributes.filter(
    (attr) => attr.attributeType !== 'file' && attr.attributeType !== 'file_multi'
  );

  const attributeItems = filteredAttributes.map((attr) => {
    let format, example;

    switch (attr.attributeType) {
      case 'date':
        format = t('date');
        example = '2023-06-30';
        break;
      case 'user':
        format = t('stringText');
        example = 'john@email.com';
        break;
      case 'text':
        format = t('stringText');
        example = 'Some text';
        break;
      case 'number':
        format = t('number');
        example = '42';
        break;
      case 'decimal':
        format = t('decimal');
        example = '3.14';
        break;
      default:
        format = t('stringText');
        example = 'Some text';
        break;
    }
    return {
      column: attr.title,
      format: format,
      example: example,
      required: attr.isRequired,
    };
  });

  const items = [...baseItems, ...attributeItems];

  return (
    <Stack space={6}>
      <ImportToolStepper currentIndex={0} />
      <div>
        <Text text={t('getReadyToImport')} font="bold14" />
        <Text text={t('followingColumns')} font="regular14" color="textSecondary" />
      </div>
      <Table>
        <thead>
          <tr>
            <th>{t('columnName')}</th>
            <th>{t('format')}</th>
            <th>{t('example')}</th>
            <th>{t('required')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.column}>
              <td>
                <BreakableText>{item.column}</BreakableText>
              </td>
              <td>{item.format}</td>
              <td>{item.example}</td>
              <td>
                {item.required ? (
                  <Text font="semibold14" color="elementGreen200" text={t('core.yes')} />
                ) : (
                  <Text font="semibold14" color="elementRed100" text={t('core.no')} />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Stack space={4} orientation="horizontal" justify="flex-end">
        <Button variant="secondary" text={t('core.cancel')} onClick={() => hideModal()} />
        <Button
          variant="primary"
          text={t('core.continue')}
          iconRight={<IconArrowRight />}
          onClick={() => setStep('uploadFile')}
        />
      </Stack>
    </Stack>
  );
};

export const UploadFile = ({
  setHeaders,
  setData,
  setStep,
  onChange,
}: {
  setHeaders: (value: string[]) => void;
  setData: (value: { [key: string]: string }[]) => void;
  setStep: (value: StepType) => void;
  onChange?: (file: File | null) => void;
}) => {
  const { t } = useScopedTranslate(locale);
  const [file, setFile] = useState<File | null>(null);
  const [chosenDelimiter, setChosenDelimiter] = useState<string>(',');
  const [hasInvalidFile, setHasInvalidFile] = useState<boolean>(false);

  const onContinue = () => {
    handleFile(file);
    setStep('matchColumns');
  };

  const handleFile = (file: File | null) => {
    if (file) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        delimiter: chosenDelimiter,
        complete: function (results: ParseResult<{ [key: string]: string }>) {
          setHeaders(results.meta.fields ?? []);
          setData(results.data ?? []);
        },
      });
    }
  };

  const delimiterOptions = [
    { value: ',', label: t('comma') },
    { value: ';', label: t('semicolon') },
  ];
  return (
    <Stack space={6}>
      <ImportToolStepper currentIndex={1} />
      <Stack space={2}>
        <FileField
          label="Click to choose a file"
          onChange={(e) => {
            setHasInvalidFile(getFileExtension(e?.name) !== 'csv');
            setFile(e);
            onChange?.(e);
          }}
          accept=".csv"
        />
        {hasInvalidFile && <FormError text={t('invalidFile')} />}
      </Stack>
      <Select
        isClearable={false}
        id="delimiter-select"
        defaultValue={chosenDelimiter}
        label={t('delimiter')}
        options={delimiterOptions}
        onChange={(option) => {
          setChosenDelimiter(option);
        }}
      />
      <Stack orientation="horizontal" justify="space-between">
        <Button
          variant="secondary"
          text={t('core.back')}
          onClick={() => setStep('gettingStarted')}
        />
        <Stack space={4} orientation="horizontal">
          <Button variant="secondary" text={t('core.cancel')} onClick={hideModal} />
          <Button
            variant="primary"
            text={t('core.continue')}
            iconRight={<IconArrowRight />}
            disabled={!file || hasInvalidFile}
            onClick={onContinue}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export const MatchColumns = <T extends ImportColumn>({
  headers,
  columns,
  setColumns,
  setStep,
}: {
  headers: string[];
  columns: T[];
  setColumns: (columns: T[]) => void;
  setStep: (value: StepType) => void;
}) => {
  const { t } = useScopedTranslate(locale);
  const options = headers.map((item) => ({ value: item, label: item }));
  const [isInvalid, setIsInvalid] = useState(false);

  const updateOptions = (id: string, value: string) => {
    const newColumns = [...columns];
    const column = newColumns.find((item) => item.id === id);
    if (column) {
      column.value = value;
      setColumns(newColumns);
    }
  };

  const checkIfValid = () => {
    if (columns.filter((column) => column.required && !column.value).length > 0) {
      setIsInvalid(true);
    } else {
      setStep('import');
    }
  };
  return (
    <Stack space={6}>
      <ImportToolStepper currentIndex={2} />
      <div>
        <Text text={t('matchRelevantHeaders')} font="bold14" />
        <Text text={t('matchHeadersDescripton')} font="regular14" color="textSecondary" />
      </div>
      <Stack space={4}>
        <Table>
          <thead>
            <tr>
              <th># </th>
              <th>{t('csvHeaders')}</th>
              <th>{t('match')}</th>
            </tr>
          </thead>
          <tbody>
            {columns.map((column, index) => (
              <tr key={`match-${column.id}`}>
                <td>{index + 1}</td>
                <td>
                  <BreakableText>{column.label}</BreakableText>
                </td>
                <td>
                  <div style={{ minWidth: pxToRem(240), maxWidth: '100%' }}>
                    <Select
                      options={options}
                      onChange={(value: string) => updateOptions(column.id, value)}
                      defaultValue={column.value}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        {isInvalid && (
          <Alert variant="error">
            <FormError text={t('requiredFieldMissing')} />
            <ul>
              {columns
                .filter((column) => column.required)
                .map((column) => (
                  <li key={`invalid-${column.id}`}>
                    <Text color="elementRed100" font="regular14" text={column.label} />
                  </li>
                ))}
            </ul>
          </Alert>
        )}
      </Stack>
      <Stack orientation="horizontal" justify="space-between">
        <Button variant="secondary" text={t('core.back')} onClick={() => setStep('uploadFile')} />
        <Stack space={4} orientation="horizontal">
          <Button variant="secondary" text={t('core.cancel')} onClick={hideModal} />
          <Button
            variant="primary"
            text={t('core.continue')}
            iconRight={<IconArrowRight />}
            onClick={checkIfValid}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export type MutationErrorType = {
  travellers?: { [key: string]: string[] }[];
};

export const Import = ({
  columns,
  data,
  mutationErrors,
  isLoading,
  isJourney = false,
  setStep,
  create,
}: {
  columns: ImportColumn[];
  data: Record<string, string>[];
  isLoading: boolean;
  mutationErrors: MutationErrorType;
  isJourney?: boolean;
  setStep: (value: StepType) => void;
  create: (data: {
    travellers: BulkCreateTraveller[];
    isSave: boolean;
    updateExisting?: boolean;
  }) => Promise<void>;
}) => {
  const [isCreating, setIsCreating] = useState(false);
  const { t } = useScopedTranslate(locale);
  const [updateExisting, setUpdateExisting] = useState(false);
  const errorList = useMemo(() => {
    const errors: string[] = [];
    data.forEach((row, index) => {
      columns.forEach((column) => {
        const rowNumber = `${index + 1}`;
        const columnName = column.value;

        if (column.required && !row[columnName]) {
          errors.push(t('emptyRowError', { rowNumber, columnName }));
        }
      });
    });
    return errors;
  }, [data, columns, t]);

  const mutationErrorList = useMemo(() => {
    const errors: string[] = [];
    (mutationErrors.travellers ?? []).forEach((traveller, idx) => {
      Object.keys(traveller).forEach((key) => {
        const column = columns.find((c) => camelize(c.id) === camelize(key));
        const rowNumber = idx + 1;
        const columnName = column?.label ?? t('unknown');
        switch (column?.type) {
          case 'email':
            errors.push(
              t('invalidEmailError', {
                rowNumber,
                columnName,
              })
            );
            break;
          case 'phone':
            errors.push(
              t('invalidPhoneError', {
                rowNumber,
                columnName,
              })
            );
            break;
          case 'date':
            errors.push(
              t('invalidDateError', {
                rowNumber,
                columnName,
              })
            );
            break;
          case 'user':
            errors.push(
              t('invalidUserError', {
                rowNumber,
                columnName,
              })
            );
            break;
          case 'single_select':
            errors.push(
              t('invalidOptionError', {
                rowNumber,
                columnName,
              })
            );
            break;
          default:
            errors.push(
              t('invalidMutationError', {
                rowNumber,
                columnName,
                errorText: traveller[key].join(','),
              })
            );
        }
      });
    });
    return errors;
  }, [mutationErrors, t, columns]);

  const getTravellers = () => {
    const travellers: BulkCreateTraveller[] = [];
    data.forEach((row) => {
      const item: BulkCreateTraveller = {
        name: '',
        email: '',
        attributes: [],
        companyTravellerAttributes: [],
      };
      columns.forEach((column) => {
        if (column.value?.length > 0) {
          const columnId = column.id;
          const value =
            typeof row[column.value] === 'string' &&
            row[column.value].startsWith("'") &&
            DANGEROUS_START_CHARACTERS.some((c) => row[column.value][1] === c)
              ? row[column.value].slice(1)
              : row[column.value];
          if (startsWith(columnId, journeyAttributePrefix)) {
            item.attributes.push({
              journey_attribute_short_id: column.id.replace(journeyAttributePrefix, ''),
              value: value,
            });
          } else if (startsWith(columnId, companyTravellerAttributePrefix)) {
            item.companyTravellerAttributes.push({
              company_traveller_attribute_short_id: column.id.replace(
                companyTravellerAttributePrefix,
                ''
              ),
              value: value,
            });
          } else {
            item[columnId as keyof BulkCreateTravellerBase] = value;
          }
        }
      });
      if (item.name) {
        travellers.push(item);
      }
    });
    return travellers;
  };

  const bulkCreate = async () => {
    setIsCreating(true);
    await create({ travellers: getTravellers(), isSave: true, updateExisting });
    setStep('importing');
  };

  const bulkValidate = async () => {
    await create({ travellers: getTravellers(), isSave: false });
  };

  useEffect(() => {
    bulkValidate();
    // eslint-disable-next-line
  }, []);

  const hasErrors = errorList.length > 0 || mutationErrorList.length > 0;

  return (
    <Stack space={6}>
      <ImportToolStepper currentIndex={3} />
      {hasErrors ? (
        <ErrorValidatingCSV errorList={errorList} mutationErrorList={mutationErrorList} />
      ) : (
        <>
          {isLoading && isCreating && <Spinner />}
          {isLoading && !isCreating ? (
            <LoadingValidatingCSV />
          ) : (
            <Stack
              justify="center"
              align="center"
              space={6}
              style={{
                ...(isCreating && {
                  opacity: 0.5,
                  pointerEvents: 'none',
                }),
              }}
            >
              <IconCheckmark color="green100" size={64} />
              <Stack align="center">
                <Text font="bold16" text={`${t('validated')}!`} />
                <Text font="regular14" color="textSecondary" text={t('allDataValid')} />
              </Stack>
              <Card variant="transparent" space={2}>
                <Checkbox
                  value={updateExisting}
                  onChange={setUpdateExisting}
                  labelVisible={true}
                  label={t('updateExisting')}
                  isDisabled={isCreating}
                />
                <div style={{ paddingLeft: theme.space[8] }}>
                  <Text
                    font="regular14"
                    color="textSecondary"
                    text={
                      isJourney
                        ? t('updateExistingDescriptionJourney')
                        : t('updateExistingDescription')
                    }
                  />
                </div>
              </Card>
            </Stack>
          )}
        </>
      )}
      <Stack orientation="horizontal" justify="space-between">
        <Button variant="secondary" text={t('core.back')} onClick={() => setStep('uploadFile')} />
        <Stack space={4} orientation="horizontal">
          <Button variant="secondary" text={t('core.cancel')} onClick={() => hideModal()} />
          <Button
            variant="primary-blue"
            text={t('import')}
            iconRight={<IconUpload />}
            onClick={() => bulkCreate()}
            isDisabled={hasErrors || isLoading}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export const LoadingValidatingCSV = () => {
  const { t } = useScopedTranslate(locale);
  return (
    <Stack justify="center" align="center" space={6}>
      <RelativeSpinner />
      <Stack align="center">
        <Text font="bold16" text={t('validatingCSV')} />
        <Text font="regular14" color="textSecondary" text={t('pleaseWaitWhileValidate')} />
      </Stack>
    </Stack>
  );
};

export const ErrorValidatingCSV = ({
  errorList,
  mutationErrorList,
}: {
  errorList?: string[];
  mutationErrorList?: string[];
}) => {
  const { t } = useScopedTranslate(locale);
  return (
    <Stack space={4}>
      <Alert variant="error">
        <FormError text={t('invalidRows')} />
        <ErrorList>
          {errorList &&
            errorList.map((error, idx) => (
              <Text color="elementRed100" font="regular14" text={error} key={`error-${idx}`} />
            ))}
          {mutationErrorList &&
            mutationErrorList.map((error, idx) => (
              <Text color="elementRed100" font="regular14" text={error} key={`error-${idx}`} />
            ))}
        </ErrorList>
      </Alert>
      <Text font="regular14" text={t('fixErrorsToImport')} />
    </Stack>
  );
};

export const Importing = () => {
  const { t } = useScopedTranslate(locale);
  return (
    <Stack space={6}>
      <div>
        <Text text={t('importingProcessed')} />
        <Text text={t('importTime')} color="textSecondary" />
      </div>
      <Stack orientation="horizontal" justify="flex-end">
        <Button variant="secondary" text={t('core.close')} onClick={() => hideModal()} />
      </Stack>
    </Stack>
  );
};

export const Table = styled.table({
  width: '100%',
  font: theme.font.regular14,
  backgroundColor: theme.color.uiTrans5,
  padding: `${pxToRem(8)} ${pxToRem(16)}`,
  borderRadius: pxToRem(8),
  thead: {
    tr: {
      th: {
        textTransform: 'uppercase',
        textAlign: 'left',
        padding: pxToRem(8),
      },
    },
  },
  tbody: {
    tr: {
      td: {
        padding: pxToRem(8),
        borderTop: `${pxToRem(1)} solid ${theme.color.border10}`,
      },
    },
  },
});

const ErrorList = styled.div({
  paddingLeft: pxToRem(24),
  maxHeight: pxToRem(300),
  overflow: 'auto',
});

const BreakableText = styled.div({
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
});

export const locale = {
  en: {
    formatGuidelines: 'Format guidelines',
    formatDescription:
      'Before you import we recommend you read up on the format guidelines and use the provided template (download below)',
    csvTemplate: 'CSV Template',
    csvTemplateDescription:
      'Automatically generated template which includes all your traveller attributes',
    csvTemplateDescriptionJourney:
      'Automatically generated template which includes all relevant traveller and journey attributes for this specific journey',
    uploadCSV: 'Upload CSV',
    uploadCSVDescription: 'Upload the CSV file with the individuals who should be imported',
    uploadCSVDescriptionJourney:
      'Upload the CSV file with the individuals who should be added to this journey',
    openImportTool: 'Open CSV import tool',
    fullName: 'Full name',
    email: 'Email',
    stringText: 'string (text)',
    number: 'number',
    decimal: 'decimal',
    date: 'Date',
    import: 'Import',
    gettingStarted: 'Getting started',
    uploadFile: 'Upload file',
    matchColumns: 'Match columns',
    getReadyToImport: 'Get ready to import your data',
    followingColumns: 'The following columns are supported, some are required.',
    columnName: 'Column name',
    format: 'Format',
    example: 'Example',
    required: 'Required',
    matchRelevantHeaders: 'Match the relevant column headers',
    matchHeadersDescripton:
      'If you used the template this should be automatically matched\nColumns you do not match are not imported',
    csvHeaders: 'CSV Headers',
    match: 'Match',
    requiredFieldMissing: 'Required field missing, please match all required fields',
    emptyRowError: 'Row {rowNumber} Column {columnName} is empty but is required',
    invalidEmailError: 'Row {rowNumber} Column {columnName} is not a valid email',
    invalidPhoneError: 'Row {rowNumber} Column {columnName} is not a valid mobile number',
    invalidDateError:
      'Row {rowNumber} Column {columnName} is not a valid date or wrong format. The format should be YYYY-MM-DD',
    invalidUserError:
      'Row {rowNumber} Column {columnName} is not a valid user, email not found in user list',
    invalidOptionError: 'Row {rowNumber} Column {columnName} is not a valid option',
    invalidMutationError:
      'Row {rowNumber} Column {columnName} got the following error "{errorText}"',
    unknown: 'Unknown',
    invalidRows: 'Invalid rows',
    fixErrorsToImport: 'To import, please fix these errors, save and re-upload the csv',
    validatingCSV: 'Validating CSV',
    pleaseWaitWhileValidate: 'Please wait while we validate the CSV data',
    validated: 'Validated',
    allDataValid: 'All matched rows and data are valid and can be imported',
    importingProcessed: 'Importing is being processed in the background',
    importTime:
      'Please note this can take some time depending on how many travellers are being added',
    updateExisting: 'Update any existing travellers',
    updateExistingDescription:
      'If any travellers imported already exist, their information will be replaced with the data from this import',
    updateExistingDescriptionJourney:
      'If any travellers imported already exist, their information will be replaced with the data from this import otherwise they will be created again',
    delimiter: 'Delimiter',
    comma: 'Comma (,) • Default',
    semicolon: 'Semicolon (;)',
    invalidFile: 'Invalid file',
  },
};
